import {
  MouseEventHandler,
  ReactElement,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { usePageContext, useReportId } from "@ryde/redux";
import { getGridLabelKey, reportFiltersWithValuesAF } from "@ryde/shared";

import {
  FreeTextFieldInputStyled,
  FreeTextFieldLabel,
  FreeTextFieldReportWrapper,
} from "./filter-free-text.report.style";
import { IFilterByIdsReportProps } from "./filters-by-ids.report.models";
import { useTableFiltersActionsReports } from "./use-table-filters-actions-reports";

export const FilterFreeTextReport = ({
  templateKey,
  initialCheckedValues,
  onValuesChange,
  onRemove,
}: IFilterByIdsReportProps): ReactElement => {
  const { entity } = usePageContext();
  const { reportId } = useReportId();
  const filtersObj = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation();

  const { setValuesToFilter, handleRemoveFilter } =
    useTableFiltersActionsReports();
  const freeTextFieldRef = useRef<HTMLInputElement>(null);

  const filterValue = useMemo(() => {
    const raw = onValuesChange
      ? initialCheckedValues
      : (filtersObj ?? {})?.[templateKey];
    return (Array.isArray(raw) ? raw[0] : raw) ?? "";
  }, [onValuesChange, initialCheckedValues, filtersObj, templateKey]);

  const label = useMemo((): string => {
    return t(getGridLabelKey(entity, templateKey));
  }, [entity, t, templateKey]);

  const removeFilter: MouseEventHandler = e => {
    e.stopPropagation();

    if (onRemove) {
      onRemove({ templateKey });
      return;
    }

    handleRemoveFilter(templateKey);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const values = [event.target.value];

    if (onValuesChange) {
      onValuesChange({ templateKey, values });
      return;
    }

    setValuesToFilter(templateKey, values);
  };

  const onBlur = (): void => {
    setIsFocused(false);
  };

  return (
    <FreeTextFieldReportWrapper
      onClick={() => {
        freeTextFieldRef.current?.focus();
      }}
    >
      <FreeTextFieldLabel htmlFor={label}>{label}</FreeTextFieldLabel>
      <FreeTextFieldInputStyled
        inputRef={freeTextFieldRef}
        name={label}
        focused={isFocused}
        placeholder="--"
        value={filterValue}
        onBlur={onBlur}
        onFocus={() => setIsFocused(true)}
        onChange={onChange}
      />
      <SVG_MUI.CircleXMark
        className="circle-x-mark"
        onClick={e => removeFilter(e)}
      />
    </FreeTextFieldReportWrapper>
  );
};
